import React, { useEffect, useState } from 'react'
import { useActivityType } from '../Hooks/ActivityTypeContext.js'
import PropTypes from 'prop-types'
import InputGroup from '../../EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import { postMFA } from '../Data/LoginDatasource.js'
import {
  FORM_NAME_CHALLENGE_CHOICE,
  FORM_NAME_LOGIN_FORM,
} from '../Helpers/LoginHelper.js'
import { makeMfaAnswerCall } from '../Helpers/MfaHelper.js'
import NewUser from '../Static/NewUser.jsx'
import FormNotification from '../../SelfService/FormNotification.jsx'

function ChallengeQuestion({
  handleDisplayChange,
  challengeQuestionData,
  mode = 'default',
  loginInput,
  showNewUser = true,
}) {
  const [challengeQuestion, setChallengeQuestion] = useState('')
  const [challengeAnswerInput, setChallengeAnswerInput] = useState('')
  const [formErrorFlag, setFormErrorFlag] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')

  const activityType = useActivityType().activityType

  const RESPONSE_CODES = {
    CREDENTIALS_NOT_ACCEPTED: '7001-3026',
    SSO_LOCKED: '7001-2029',
    BLOCKED_MFA: '7001-3027',
    LOCKED_MFA: '7001-3027',
  }

  const [formValidation, setFormValidation] = useState({
    'login-security-question-answer': {
      error: false,
      errorMessage: 'Your answer does not match our records. Please try again.',
    },
  })

  useEffect(() => {
    const initChallengeQuestion = async () => {
      if (challengeQuestionData) {
        const data = await postMFA(challengeQuestionData, activityType)
        if (data && data.value && data.value.mfa && data.value.mfa.length > 0) {
          setChallengeQuestion(data.value.mfa[0].question)
        }
      }
    }
    initChallengeQuestion()
  }, [challengeQuestionData])

  const onChangeChallengeAnswerInput = (event) => {
    setChallengeAnswerInput(event.target.value)
  }

  const continueClicked = () => {
    if (!challengeAnswerInput) {
      const _formValidation = { ...formValidation }
      _formValidation['login-security-question-answer'].error = true
      setFormValidation(_formValidation)
    } else {
      const data = {
        answer: challengeAnswerInput,
      }
      makeMfaAnswerCall(
        data,
        loginInput,
        mode,
        handleDisplayChange,
        handleError,
        activityType
      )
    }
  }

  const cancelClicked = () => {
    setChallengeAnswerInput('')
    handleDisplayChange(FORM_NAME_LOGIN_FORM)
  }

  const onDontRememberAnswerClick = () => {
    setChallengeAnswerInput('')
    handleDisplayChange(FORM_NAME_CHALLENGE_CHOICE)
  }

  const handleError = (errorCode) => {
    const _formValidation = { ...formValidation }
    switch (errorCode) {
      case RESPONSE_CODES.CREDENTIALS_NOT_ACCEPTED:
        // field level error
        _formValidation['login-security-question-answer'].error = true
        _formValidation['login-security-question-answer'].errorMessage =
          'Your answer does not match our records. Please try again.'
        setFormValidation(_formValidation)
        break
      case RESPONSE_CODES.SSO_LOCKED:
      case RESPONSE_CODES.BLOCKED_MFA:
      case RESPONSE_CODES.LOCKED_MFA:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'For your protection, your account has been locked because you did not enter your passcode correctly. To unlock your account, please <a href="/about-us/contact-us/">contact a Columbia Threadneedle</a> representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a>800.248.2828</a> for assistance.'
        )
        break
      default:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'A problem occured with your account. Please call us at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> or check your email for further instruction.'
        )
    }
  }

  return (
    <div
      className="u-bgColorLightest u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft0 u-borderRight0 u-padding8gu u-marginBottom10gu"
      data-analytics-login-step="Answer Security Question"
    >
      <div className="Grid">
        <div className="u-sizeFull u-lg-size5of12 u-lg-after1of12 u-marginBottom6gu u-lg-marginBottom0 Content">
          <h2 className="u-marginTop0 u-marginBottom1gu">
            Please answer your question
          </h2>
          <legend className="u-marginBottom4gu">
            To verify your identity, please answer your security question:
          </legend>
          {formErrorFlag && (
            <FormNotification content={formErrorMessage} isHtml={true} />
          )}
          <div className="Input-group u-marginBottom4gu">
            <label className="Input-label">Question</label>
            <p>{challengeQuestion}</p>
          </div>
          <InputGroup
            handleInputChange={onChangeChallengeAnswerInput}
            formValidation={formValidation}
            fieldName="login-security-question-answer"
            fieldId="login-security-question-answer"
            label="Answer"
            compact={false}
          />
          <div className="Grid Grid--withGutter">
            <div className="u-size6of12">
              <button
                onClick={continueClicked}
                className="Button Button--primary Button--block u-marginRight5gu u-marginBottom3gu u-textNoTransform"
                data-analytics="login-security-question-continue"
              >
                Continue
              </button>
            </div>
            <div className="u-size6of12">
              <button
                onClick={cancelClicked}
                className="Button Button--secondary Button--block u-paddingHoriz8gu u-decoBorderBrand u-textNoTransform"
                data-analytics="login-security-question-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
          <a
            href="#"
            onClick={onDontRememberAnswerClick}
            className="Link u-block"
            data-analytics="login-security-forgot-link"
          >
            Don&rsquo;t remember your answer?
          </a>
        </div>
        {showNewUser && (
          <div className="u-sizeFull u-lg-before1of12 u-lg-size5of12 Content">
            <NewUser />
          </div>
        )}
      </div>
    </div>
  )
}

ChallengeQuestion.propTypes = {
  handleDisplayChange: PropTypes.func,
  challengeQuestionData: PropTypes.string,
  mode: PropTypes.string,
  loginInput: PropTypes.string,
  showNewUser: PropTypes.bool,
}

export default ChallengeQuestion
